import {
  type AgentCoachResponse,
  type CoachingHistoryResponsePagedData,
  type CoachResponse,
  type HeadlineResponsePersonBaseRelatedPagedData,
  type PlayerProfileResponse,
  type QuoteResponsePagedData,
  rdbV1CoachesAgentsDetail,
  rdbV1CoachesHistoryDetail,
  rdbV1CoachesProfileDetail,
  rdbV1QuotesList,
  v1PeopleHeadlinesDetail,
} from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import withAuthentication from '@on3/ui-lib/src/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/src/hocs/withValidUrl';
import { getFulfilledValue, proxyApi } from '@on3/ui-lib/utils/api';
import { getIdFromParam } from '@on3/ui-lib/utils/url';
import { CoachProfile } from 'components/Db/Profile/Coach/CoachProfile';
import { DatabaseLayout } from 'components/Layouts/DatabaseLayout';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import type { GetServerSideProps } from 'next';
import { playerModules } from 'utils/player';

export interface ICoachProfilePageProps {
  coach: CoachResponse;
  coachHistory?: CoachingHistoryResponsePagedData | null;
  coachArticles?: HeadlineResponsePersonBaseRelatedPagedData | null;
  coachQuotes?: QuoteResponsePagedData | null;
  coachAgent?: AgentCoachResponse | null;
  player?: PlayerProfileResponse;
}

interface ICoachProfilePage extends IGenericPageProps, ICoachProfilePageProps {}

const CoachProfilePage = ({
  coach,
  coachHistory,
  coachArticles,
  coachQuotes,
  coachAgent,
  player,
  siteData,
  ...props
}: ICoachProfilePage) => {
  const hasJob = !!coach.organization;
  const title = hasJob
    ? `${coach?.fullName} - ${coach.organization?.fullName} - ${coach.primaryPosition?.name} | On3.com`
    : `${coach?.fullName} - Coach Profile - On3.com`;
  const description = `Discover ${coach?.fullName}'s coaching journey at On3.com. Learn about his coaching style, career highlights, and contributions to the team.`;

  return (
    <PageWrapper
      {...props}
      description={description}
      ogType="profile"
      siteData={siteData}
      title={title}
    >
      <DatabaseLayout>
        <CoachProfile
          coach={coach}
          coachAgent={coachAgent}
          coachArticles={coachArticles}
          coachHistory={coachHistory}
          coachQuotes={coachQuotes}
          player={player}
        />
      </DatabaseLayout>
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  const { id } = ctx.query as { id: string };
  const personKey = getIdFromParam(id);

  if (!siteData || !personKey) {
    return {
      notFound: true,
    };
  }

  try {
    const [coach] = await Promise.allSettled([
      rdbV1CoachesProfileDetail(proxyApi, { personKey }),
    ]);

    const coachData = getFulfilledValue(coach);

    if (!coachData) {
      return {
        notFound: true,
      };
    }

    const [coachHistory, coachArticles, coachQuotes, coachAgent, player] =
      await Promise.allSettled([
        rdbV1CoachesHistoryDetail(proxyApi, { personKey }),
        rdbV1QuotesList(proxyApi, { personKey }),
        v1PeopleHeadlinesDetail(proxyApi, { key: personKey, limit: 6 }),
        rdbV1CoachesAgentsDetail(proxyApi, { coachKey: personKey }),
        playerModules.player(proxyApi, { personKey }),
      ]);

    const data = {
      pageType: 'database',
      siteData,
      userData: userData || {},
      coach: coachData,
      coachHistory: getFulfilledValue(coachHistory),
      coachArticles: getFulfilledValue(coachArticles),
      coachQuotes: getFulfilledValue(coachQuotes),
      coachAgent: getFulfilledValue(coachAgent),
      player: getFulfilledValue(player),
    };

    return { props: { ...data } };
  } catch (e) {
    return { props: {} };
  }
};

export default withValidUrl(CoachProfilePage);
